import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import FeedbackResponseForm, { QuestionResponse } from './FeedbackResponseForm'
import { questionsToAnswer } from './questionSnapshot'
import { feedbackService } from '@/services/api/feedbackService'
import type { FeedbackRequest } from '@/services/api/feedbackService'
import { integrationService } from '@/services/api/integrationService'
import { Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '@/providers/AuthProvider'

interface FeedbackResponseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  requestId: string
  onSuccess?: () => void
}

export default function FeedbackResponseDialog({
  open,
  onOpenChange,
  requestId,
  onSuccess
}: FeedbackResponseDialogProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [request, setRequest] = useState<FeedbackRequest | null>(null)
  const [employeeName, setEmployeeName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open && requestId && user?.employeeId) {
      loadRequest()
    }
  }, [open, requestId, user?.employeeId])

  const loadRequest = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await feedbackService.getFeedbackRequest(requestId, user?.employeeId)
      
      if (import.meta.env.DEV) console.log('📋 Loaded feedback request:', data)
      
      // The requester's final list when the request carries one; otherwise the
      // template's questions (older requests and campaigns), as before.
      data.custom_questions = questionsToAnswer(data)
      if (data.custom_questions.length === 0) {
        console.warn('⚠️ No template or custom questions found')
      }
      
      setRequest(data)

      // Subject name resolution.
      //
      // For CGM-style campaigns (subject_relationship='campus_general_manager')
      // the backend sets `requested_for_user_id = respondent's own employee_id`
      // as a placeholder — the actual CGM target is captured in the survey's
      // first question. We must NOT call F&O for those (it 404s and is
      // misleading anyway). We display a generic role label instead.
      //
      // Detection rule: the request is a placeholder-subject one when it
      // belongs to a campaign AND the subject (requested_for_user_id) is
      // ALSO one of the stakeholders responding to it. This avoids relying
      // on `user.employeeId` being loaded at this point, which it sometimes
      // isn't on slow auth init.
      const stakeholderIds: string[] = Array.isArray(data.stakeholders)
        ? data.stakeholders.map((s: any) => s.stakeholder_user_id).filter(Boolean)
        : []
      const isSelfTargetedCampaign =
        !!data.campaign_id &&
        !!data.requested_for_user_id &&
        (stakeholderIds.includes(data.requested_for_user_id) ||
          data.requested_for_user_id === user?.employeeId)

      if (data.campaign_subject_relationship === 'organisation') {
        // Organisation-wide survey (e.g. Culture Survey) — no subject at
        // all. The name satisfies the render gate; the form hides the
        // "About <X>" line for this context anyway.
        setEmployeeName('Eduvos')
      } else if (isSelfTargetedCampaign) {
        // Generic label keeps the form's "About <X>" line meaningful without
        // naming any specific person. The respondent picks the actual CGM in
        // their first answer below.
        setEmployeeName('your Campus General Manager')
      } else if (data.requested_for_user_id) {
        try {
          const employee = await integrationService.getEmployeeByPersonnelNumber(data.requested_for_user_id)
          // ALWAYS set a name. The render gates the entire form on
          // employeeName — a null here left the dialog as an infinite
          // spinner whenever the subject's PN wasn't resolvable via the
          // integration search (test personas, workers missing from F&O),
          // which presented as "the feedback dialog doesn't open".
          setEmployeeName(employee?.full_name || employee?.personnel_number || data.requested_for_user_id)
        } catch (err) {
          console.error('Failed to load employee name:', err)
          setEmployeeName(data.requested_for_user_id)
        }
      } else {
        // Subject-less request shapes (e.g. requester-is-subject) — never
        // leave employeeName null for the same render-gate reason.
        setEmployeeName('your colleague')
      }
    } catch (err: any) {
      console.error('Failed to load feedback request:', err)
      setError(err.response?.data?.detail || 'Failed to load feedback request')
      toast.error('Failed to load feedback request')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (responseData: QuestionResponse[]) => {
    if (!request) return

    try {
      setSubmitting(true)

      // Find the stakeholder record for current user
      const currentUserId = user?.employeeId
      if (!currentUserId) {
        throw new Error('User not authenticated')
      }

      const stakeholderData = request.stakeholders?.find(
        s => s.stakeholder_user_id === currentUserId
      )

      if (!stakeholderData?.id) {
        throw new Error('Stakeholder information not found. You may not have permission to respond to this request.')
      }

      // The "Overall impression" rating + "Anything else?" comments
      // sections were removed from the form (HR wanted only template
      // questions). The submit payload no longer carries those fields.
      await feedbackService.submitFeedbackResponse({
        feedback_request_id: request.id!,
        feedback_stakeholder_id: stakeholderData.id,
        is_anonymous: request.is_anonymous,
        response_data: responseData,
      })

      toast.success('Feedback submitted successfully!')
      onOpenChange(false)
      onSuccess?.()
    } catch (err: any) {
      console.error('Failed to submit feedback:', err)
      toast.error(err.response?.data?.detail || 'Failed to submit feedback')
      // Rethrow so the form knows the submit FAILED and keeps its local
      // draft — swallowing it here made the form clear answers on failure.
      throw err
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* A click outside a 70-question survey must not dismiss it — that was
          exactly how respondents lost their progress (drafts now persist as a
          second line of defence). Escape and the explicit Cancel still close. */}
      <DialogContent
        className="max-w-[940px] w-[94vw] max-h-[92vh] overflow-y-auto p-0 sm:rounded-lg"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* The form below owns all visible chrome (editorial header + form
            body + sticky submit footer). The DialogHeader is kept only for
            Radix a11y — visually hidden so the form's typography reads
            uninterrupted. */}
        {/* Error must render BEFORE the loading/name gate — on a failed load
            employeeName stays null, and the old order trapped the dialog on
            the spinner with the error message unreachable. */}
        {(!error && (loading || !employeeName)) ? (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Loading Feedback Request</DialogTitle>
              <DialogDescription>Please wait while we load the details</DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-heritage-blue" />
            </div>
          </>
        ) : error ? (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Error</DialogTitle>
              <DialogDescription>Unable to load feedback request</DialogDescription>
            </DialogHeader>
            <div className="text-center py-20 px-6">
              <p className="text-rose-600">{error}</p>
            </div>
          </>
        ) : request ? (() => {
            // Confidentiality banner context.
            // CGM campaigns are detected via the same self-targeted-subject
            // heuristic we use to skip the F&O lookup above; line-manager
            // campaigns are everything else under campaign_id. Non-campaign
            // requests fall through to in-the-moment (form picks anonymous
            // vs confidential copy itself).
            const stakeholderIds: string[] = Array.isArray(request.stakeholders)
              ? request.stakeholders.map((s: any) => s.stakeholder_user_id).filter(Boolean)
              : []
            const selfTargeted =
              !!request.campaign_id &&
              !!request.requested_for_user_id &&
              (stakeholderIds.includes(request.requested_for_user_id) ||
                request.requested_for_user_id === user?.employeeId)
            const confidentialityContext = request.campaign_id
              ? (request.campaign_subject_relationship === 'organisation'
                  ? 'campaign-organisation'
                  : selfTargeted ? 'campaign-cgm' : 'campaign-line-manager')
              : 'in-the-moment'

            return (
              <>
                <DialogHeader className="sr-only">
                  <DialogTitle>Provide feedback for {employeeName || request.requested_for_user_id}</DialogTitle>
                  <DialogDescription>
                    {request.campaign_id
                      ? `Your responses are confidential and not attributed to you.`
                      : `Share your feedback for ${employeeName || request.requested_for_user_id}.`}
                  </DialogDescription>
                </DialogHeader>
                <FeedbackResponseForm
                  requestId={request.id!}
                  requestTitle={request.title}
                  requestedForUser={employeeName || request.requested_for_user_id}
                  questions={request.custom_questions || []}
                  isAnonymous={request.is_anonymous}
                  isConfidential={request.is_confidential}
                  confidentialityContext={confidentialityContext}
                  instructions={request.instructions}
                  onSubmit={handleSubmit}
                  onCancel={() => onOpenChange(false)}
                  loading={submitting}
                />
              </>
            )
          })() : null}
      </DialogContent>
    </Dialog>
  )
}
